import { useState, useEffect, useRef } from "react";
import { Button, Space, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { toastError } from "helpers/toasters";
import { getStoreSubcategories } from "Network";

// components
import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import AddEditStoreSubcategory from "./AddEditStoreSubcategory";

const StoreSubcategories = () => {
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("add");
    const [rowData, setRowData] = useState("");
    const [dataSource, setDataSource] = useState([]);
    const [categoriesFilterList, setCategoriesFilterList] = useState([]);
    const [isSubcategoryModalVisible, setIsSubcategoryModalVisible] = useState(false);
    const [renderMe, setRenderMe] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
        }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: "block",
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? "#1890ff" : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: "#ffc069",
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            key: "nameEn",
            title: "NAME (EN)",
            dataIndex: "nameEn",
            ...getColumnSearchProps("nameEn"),
        },
        {
            key: "nameAr",
            title: "NAME (AR)",
            dataIndex: "nameAr",
            ...getColumnSearchProps("nameAr"),
        },
        {
            key: "category",
            title: "CATEGORY",
            dataIndex: "category",
            filters: categoriesFilterList,
            onFilter: (value, record) => value === record.categoryId,
        },
        {
            key: "isActive",
            title: "IS ACTIVE",
            dataIndex: "isActive",
            filters: [
                {
                    text: 'Active',
                    value: true,
                },
                {
                    text: 'Deactive',
                    value: false,
                }
            ],
            onFilter: (value, record) => value === record.isActive,
            render: (_, row) => (
                <span>{row.isActive ? "Active" : "Deactive"}</span>
            ),
        },
        {
            key: "creationDate",
            title: "CREATION DATE",
            dataIndex: "creationDate",
            ...getColumnSearchProps("creationDate"),
        },
        {
            key: "id",
            title: "ACTIONS",
            dataIndex: "action",
            render: (_, row) => {
                return (
                    <Space size={5}>
                        <Button
                            onClick={() => handleEditBtn(row)}
                            style={{ backgroundColor: "green", color: "white" }}
                        >
                            Edit
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const handleEditBtn = (row) => {
        setMode("edit");
        setRowData(row);
        setIsSubcategoryModalVisible(true);
    };

    const handleAddCategories = () => {
        setIsSubcategoryModalVisible(true);
        setMode("add");
    };

    const handleModalCancel = () => {
        setIsSubcategoryModalVisible(false);
        setRowData(null)
        setMode("add");
    };

    useEffect(() => {
        getStoreSubcategories(
            (res) => {
                setLoading(false);
                if (res.success) {
                    setDataSource(res.data);
                    // set category options
                    const categoriesOptions = [];
                    res.data.forEach((sub) => {
                        const alreadyInArr = categoriesOptions.find((ele) => ele.value === sub.categoryId);
                        if (!alreadyInArr) {
                            const obj = {
                                value: sub.categoryId,
                                text: sub.category
                            }
                            categoriesOptions.push(obj);
                        }
                    })
                    setCategoriesFilterList(categoriesOptions);
                } else {
                    toastError(res.message);
                }
            },
            (res) => {
                setLoading(false);
                if (res.data?.error) {
                    toastError(res.message);
                }
            }
        );
    }, [renderMe]);

    return (
        <section className="users store-items">
            <div className="users__all-contents">
                <div className="users__content">
                    <div className="users__usersData">
                        <Header
                            title="Store Subcategories"
                            plusButtonName={"Add Subcategory"}
                            plusButtonAction={handleAddCategories}
                        />
                        <SubHeader bgColor="#356ab3" title="All Store Subcategories" />
                        <div className="users__table store-items__data-table grid-table mrg-ye-50 ">
                            <GridTable
                                loading={loading}
                                columns={columns}
                                dataSource={dataSource}
                                rowId={"id"}
                            />
                        </div>
                    </div>
                </div>
                {isSubcategoryModalVisible && (
                    <AddEditStoreSubcategory
                        isSubcategoryModalVisible={isSubcategoryModalVisible}
                        mode={mode}
                        rowData={rowData}
                        SubHeader={
                            <SubHeader
                                bgColor="#F58234"
                                title={`${mode === "add" ? "Add New Store Subcategory" : "Edit Store Subcategory"}`}
                                closeButton
                                onCancel={handleModalCancel}
                            />
                        }
                        onSubmit={() => {
                            setRenderMe(!renderMe)
                            handleModalCancel()
                        }}
                        onCancel={handleModalCancel}
                        primaryBtnName={`${mode === "add" ? "Add" : "Save"}`}
                        secondaryBtnName={"Cancel"}
                    />
                )}
            </div>
        </section>
    );
}

export default StoreSubcategories;